'use client';

/**
 * DataRoomEmptyState Component
 *
 * Empty state displayed when no documents are in the data room.
 * Shows icon, title, description, and action buttons for browse/upload.
 */

import CloudUpload from '@mui/icons-material/CloudUpload';
import Description from '@mui/icons-material/Description';
import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';

// ============================================================================
// Types
// ============================================================================

export interface DataRoomEmptyStateProps {
  /** Callback when the browse button is clicked */
  onBrowse: () => void;
  /** Callback when the upload button is clicked */
  onUpload: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const DataRoomEmptyState = ({ onBrowse, onUpload }: DataRoomEmptyStateProps): React.ReactElement => {
  const { t } = useI18n();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          mb: 2,
          color: 'text.disabled',
          '& > svg': { fontSize: iconSizes['3xl'] },
        }}
      >
        <Description />
      </Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('dataRoom.empty.title')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.disabled',
          mb: 3,
          maxWidth: 400,
        }}
      >
        {t('dataRoom.empty.description')}
      </Typography>
      <Stack direction="row" spacing={2}>
        <GlassButton variant="outlined" startIcon={<Search />} onClick={onBrowse}>
          {t('dataRoom.browse')}
        </GlassButton>
        <GlassButton variant="gradient" startIcon={<CloudUpload />} onClick={onUpload}>
          {t('dataRoom.uploadNew')}
        </GlassButton>
      </Stack>
    </Box>
  );
};

DataRoomEmptyState.displayName = 'DataRoomEmptyState';

export default DataRoomEmptyState;
